import { SessionExpiredError } from "../../types";
import type { ProjectCreation } from "../admin-types";
import { PermissionError } from "../errors";
import { sessionClock, type SessionLimits } from "../logic/session-clock";
import { randomToken, type RandomSource } from "../logic/tokens";
import type { OrgRole, Permission, PlanId } from "../types";
import type { IdentityState, StoredOrganisation, StoredSession, StoredUser } from "./state";

/** Shared state and helpers of the mock identity service. Each area (auth, organisation, staff…) gets this. */

export interface Persistence {
  load(): IdentityState | undefined;
  save(state: IdentityState): void;
  /** Calls back when another tab changed the stored state; returns a stop function. */
  watch(onChange: () => void): () => void;
}

/** Where projects live, so requests can name them and land with the right organisation. */
export interface ProjectDirectory {
  find(projectId: string): { organisationId: string; name: string } | undefined;
}

export interface MockIdentityOptions {
  latencyMs: number;
  limits: SessionLimits;
  persistence: Persistence;
  random: RandomSource;
  now: () => number;
  userAgent: string;
  projects: ProjectDirectory;
}

export interface Ctx {
  state: IdentityState;
  readonly options: MockIdentityOptions;
  readonly listeners: Set<() => void>;
  now(): number;
  wait(): Promise<void>;
  /** Save and tell session listeners (sign-in, sign-out, organisation switch, expiry). */
  commit(): void;
  /** Save without telling anyone (activity timestamps, code counters). */
  persist(): void;
  token(): string;
}

export function createCtx(options: MockIdentityOptions, state: IdentityState): Ctx {
  const listeners = new Set<() => void>();
  const ctx: Ctx = {
    state,
    options,
    listeners,
    now: options.now,
    wait: () => new Promise<void>((resolve) => setTimeout(resolve, options.latencyMs)),
    commit: () => {
      options.persistence.save(ctx.state);
      for (const listener of listeners) listener();
    },
    persist: () => {
      options.persistence.save(ctx.state);
    },
    token: () => randomToken(options.random),
  };
  return ctx;
}

/** Rights each organisation role holds in the UI (27 §5.3.1); the server re-checks every call. */
const ROLE_PERMISSIONS: Readonly<Record<OrgRole, readonly Permission[]>> = {
  owner: ["org.manage_people", "org.manage_settings", "project.manage_access", "project.create"],
  admin: ["org.manage_people", "org.manage_settings", "project.manage_access", "project.create"],
  member: ["project.create"],
  guest: [],
};

/** A Member creates projects only while the organisation lets Members (FR-ADMIN-111). */
export function permissionsFor(
  role: OrgRole | undefined,
  projectCreation: ProjectCreation = "members",
): Permission[] {
  if (role === undefined) return [];
  return ROLE_PERMISSIONS[role].filter(
    (permission) =>
      permission !== "project.create" || role !== "member" || projectCreation === "members",
  );
}

/** Plain-language name of the role a refused action needs (FR-ADMIN-022). */
const NEEDED: Readonly<Record<Permission, string>> = {
  "org.manage_people": "Owner or Admin role needed to manage people.",
  "org.manage_settings": "Owner or Admin role needed to change organisation settings.",
  "project.manage_access": "Project admin role needed to answer access requests.",
  "project.create": "Member role needed to create projects.",
};

export function findUser(ctx: Ctx, userId: string): StoredUser | undefined {
  return ctx.state.users.find((user) => user.id === userId);
}

export function userByEmail(ctx: Ctx, email: string): StoredUser | undefined {
  return ctx.state.users.find((user) => user.email === email);
}

export function findOrg(ctx: Ctx, organisationId: string): StoredOrganisation | undefined {
  return ctx.state.organisations.find((org) => org.id === organisationId);
}

export function roleIn(
  ctx: Ctx,
  userId: string,
  organisationId: string | undefined,
): OrgRole | undefined {
  if (organisationId === undefined) return undefined;
  return ctx.state.memberships.find(
    (m) => m.userId === userId && m.organisationId === organisationId,
  )?.role;
}

export type Live = { session: StoredSession; user: StoredUser } | "none" | "expired";

/** This browser's session, after applying the idle and absolute timeouts (FR-ADMIN-047). */
export function liveSession(ctx: Ctx): Live {
  const id = ctx.state.cookie;
  if (id === undefined) return "none";
  const session = ctx.state.sessions.find((s) => s.id === id);
  const user = session === undefined ? undefined : findUser(ctx, session.userId);
  if (session === undefined || user === undefined) return "none";
  if (session.endedAt !== undefined) return session.endReason === "signed_out" ? "none" : "expired";
  if (sessionClock(session, ctx.options.limits, ctx.now()) !== "active") {
    session.endedAt = ctx.now();
    session.endReason = "expired";
    ctx.commit();
    return "expired";
  }
  return { session, user };
}

/** The live session for an authenticated call. Counts as activity, which moves the idle timeout. */
export function requireSession(ctx: Ctx): { session: StoredSession; user: StoredUser } {
  const live = liveSession(ctx);
  if (live === "none" || live === "expired") throw new SessionExpiredError();
  live.session.lastActiveAt = Math.max(live.session.lastActiveAt, ctx.now());
  ctx.persist();
  return live;
}

export interface OrgContext {
  session: StoredSession;
  user: StoredUser;
  org: StoredOrganisation;
  role: OrgRole;
}

/** The live session in its current organisation. */
/** The organisation's plan (D-034): saved state from before plans has none recorded, which is Studio. */
export function planOf(org: StoredOrganisation): PlanId | null {
  return org.plan === undefined ? "studio" : org.plan;
}

export function requireOrg(ctx: Ctx): OrgContext {
  const { session, user } = requireSession(ctx);
  const org =
    session.organisationId === undefined ? undefined : findOrg(ctx, session.organisationId);
  const role = roleIn(ctx, user.id, org?.id);
  if (org === undefined || role === undefined)
    throw new PermissionError("You aren't a member of an organisation on Orthos.");
  const suspended = ctx.state.memberships.some(
    (m) => m.userId === user.id && m.organisationId === org.id && m.suspended === true,
  );
  if (suspended) throw new SessionExpiredError();
  // Not activated (D-034): the identity service answers 403 not_activated to everything but sign-in, sign-out and switching.
  if (planOf(org) === null) throw new PermissionError("Your organisation isn't activated yet.");
  return { session, user, org, role };
}

export function requirePermission(ctx: Ctx, permission: Permission): OrgContext {
  const context = requireOrg(ctx);
  if (!permissionsFor(context.role, context.org.policies?.projectCreation).includes(permission))
    throw new PermissionError(NEEDED[permission]);
  return context;
}

/** "Chrome" and "macOS" from a user-agent string, for the sessions list (FR-ADMIN-048). */
export function describeDevice(userAgent: string): { device: string; browser: string } {
  const browser = /Edg\//.test(userAgent)
    ? "Edge"
    : /Firefox\//.test(userAgent)
      ? "Firefox"
      : /Chrome\//.test(userAgent)
        ? "Chrome"
        : /Safari\//.test(userAgent)
          ? "Safari"
          : "Browser";
  const device = /iPhone/.test(userAgent)
    ? "iPhone"
    : /iPad/.test(userAgent)
      ? "iPad"
      : /Android/.test(userAgent)
        ? "Android phone"
        : /Mac OS X|Macintosh/.test(userAgent)
          ? "Mac"
          : /Windows/.test(userAgent)
            ? "Windows PC"
            : /Linux/.test(userAgent)
              ? "Linux PC"
              : "Computer";
  return { device, browser };
}

/** Initials for an avatar: first and last word. */
export function initialsOf(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .filter((word) => word !== "");
  const first = words[0]?.[0] ?? "";
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase() || "?";
}
